/**
 * Legacy JavaScript Entry Point
 *
 * This file serves as the entry point for the legacy JavaScript bundle.
 * Feature modules self-register their actions via registerActions().
 */

// NOTE: stylesheets are deliberately NOT imported here. They are built by
// `vite.styles.config.ts` from `src/styles/index.css` as a separate, non-lib
// build, because Vite's library mode force-inlines every `url()` asset — it
// turned flag-icons' ~540 country SVGs into data: URIs and produced a 9.7 MB
// stylesheet. The separate build emits them as real files instead.

// Plugins
import "./plugins/dropzone"; // side-effect: disables Dropzone auto-discover

// Side-effect: registers Preact modal components
import "./ui/modalRegistry";

// ModalStack — Preact component that manages the modal lifecycle
import { ModalStack } from "./ui/ModalStack";
import { h } from "preact";
import { renderIsland } from "./ui/renderIsland";
import { ID_MODALS } from "./utils/elementIds";

// Third-party globals
import { registerActions } from "./actionRegistry";
import md5 from "blueimp-md5";
registerActions({ md5 });

// Feature modules — self-register their window-exposed actions on import
import { initModal } from "./ui/modal";
import "./ui/contentLoader";
import "./ui/messages";
import "./utils/dom";
import "./features/cookies";
import "./features/search";
import "./features/toggles";
import "./features/expiredSelection";
import "./features/apiDocs";
import "./features/userAccount";
import "./features/metadataMisc";
import "./features/directorySearch";
import "./features/gallery";
import "./features/transfers";
import "./features/ecommerceTransfer";
import "./features/variant";
import "./features/mockupBrowser";
import "./utils/string";
import "./features/tagsSynonyms";
import "./features/keywords";
import "./features/resellerSettings";
import "./features/cart";
import "./features/profile";
import "./features/newsletter";
import "./features/directoryUpload";
import "./features/resellerDashboard";
import "./features/collections";
import "./features/selection";
import "./features/navigation";
import "./features/groups";
import "./features/auth";
import "./features/download";
import "./features/mockupDefaults";
import "./features/mockupManagement";
import "./features/mockupClient";
import "./features/productImages";
import "./features/productTemplate";
import "./features/pricing";
import "./features/ecommerce";
import "./features/uploadCallbacks";
import "./features/settingsSave";
import "./features/downloadActions";
import "./features/miscOperations";
import "./features/bio";
import "./features/expired";
import "./features/mockupActions";
import "./features/presignedDropzones";
import "./features/inlineHandlers";
import "./features/follow";
import "./features/briefs";
import "./features/admin";

import { VIEW_ACTIONS } from "@brave-new-world/shared/viewContract";
import { getAction } from "./actionRegistry";
import { initWindowDimensions } from "./globalState";
import { initEventHandlers } from "./eventHandlers";
import { initActionDelegator } from "./features/actionDelegator";
import { initPageInit } from "./features/pageInit";
import { initDashboard } from "./features/dashboard/init";
import { initFrontAdvanced } from "./features/frontAdvanced/init";
import { initSearchImage } from "./features/searchImage/init";
import { initBestsellers } from "./features/bestsellers/init";
import { initNotificationBell } from "./features/notificationBell";
import { initLabsSearch } from "./features/labsSearch/init";
import { initConversionTag } from "./features/conversionTag";
import { initTableSort } from "./plugins/tableSort";

// Initialize on DOM ready - only DOM-dependent initialization here
document.addEventListener("DOMContentLoaded", () => {
  // Mount Preact ModalStack into <div id="modals"></div>
  renderIsland(ID_MODALS, h(ModalStack, null));

  initModal();

  // Initialize window dimensions and mobile detection
  initWindowDimensions();

  // Initialize event handlers (keyboard, scroll, popstate)
  initEventHandlers();

  // Initialize data-action delegation for PHP inline handlers
  initActionDelegator();

  // Initialize page-level actions from data attributes (replaces inline <script> tags)
  initPageInit();

  // Mount /dashboard, which the browser renders end to end (server sends only a shell)
  initDashboard();

  // Mount the experimental /labs/search vector-search island if present
  initLabsSearch();

  // Mount the front-page Advanced search box if present
  initFrontAdvanced();
  initSearchImage();
  initNotificationBell();
  // No-ops unless the bestsellers tab bar is on the page.
  initBestsellers();

  // Push the signup conversion event from the #conversion-tag-init island
  initConversionTag();

  // Initialize table column sorting for tables with class "sortable"
  initTableSort();

  // Dev-only: every action in the shared view contract must have a
  // registration — a miss means SSR-emitted data-fn attributes silently no-op.
  if (location.hostname === "localhost") {
    for (const name of VIEW_ACTIONS) {
      if (!getAction(name)) {
        console.error(`[viewContract] action "${name}" is not registered`);
      }
    }
  }
});

declare global {
  interface Window {
    // Third-party CDN globals
    hcaptcha?: { render: (id: string, opts: Record<string, string>) => void };
  }
}
